/**
 * Retry configuration for resilience
 * 
 * Provides configurable exponential backoff strategy for retrying transient failures.
 * Error codes are categorized as retryable (network, timeouts) or non-retryable (permissions).
 */

/**
 * Default retry configuration
 * @type {Object}
 */
export const RETRY_CONFIG = {
    // Maximum number of retry attempts (0 means try once, no retries)
    maxRetries: 3,
    
    // Initial delay in milliseconds before first retry
    initialDelayMs: 100,
    
    // Multiplier for exponential backoff (delay *= multiplier after each retry)
    backoffMultiplier: 2,
    
    // Maximum delay cap (never wait longer than this)
    maxDelayMs: 5000,
    
    // Error codes that should trigger retry (transient errors)
    retryableErrorCodes: [
        'NETWORK_ERROR',
        'TIMEOUT',
        'RESOURCE_EXHAUSTED',  // Firebase rate limit
        'UNAVAILABLE',         // Service temporarily unavailable
        'DEADLINE_EXCEEDED',   // Request timeout
        'service unavailable'  // Firebase error message
    ],
    
    // Error codes where we should fail immediately (permanent errors)
    nonRetryableErrorCodes: [
        'PERMISSION_DENIED',
        'UNAUTHENTICATED',
        'INVALID_ARGUMENT',
        'NOT_FOUND',
        'ALREADY_EXISTS',
        'FAILED_PRECONDITION'
    ]
};

/**
 * Calculate delay for retry attempt using exponential backoff
 * 
 * @param {number} attemptNumber - Which attempt (0-based)
 * @param {Object} config - Retry configuration
 * @returns {number} Delay in milliseconds
 * 
 * @example
 * // First retry (attempt 0): 100ms
 * calculateBackoffDelay(0) // 100
 * 
 * // Second retry (attempt 1): 200ms
 * calculateBackoffDelay(1) // 200
 * 
 * // Third retry (attempt 2): 400ms
 * calculateBackoffDelay(2) // 400
 * 
 * // Cap at 5000ms
 * calculateBackoffDelay(10) // 5000
 */
export function calculateBackoffDelay(attemptNumber, config = RETRY_CONFIG) {
    const exponentialDelay = config.initialDelayMs * Math.pow(config.backoffMultiplier, attemptNumber);
    return Math.min(exponentialDelay, config.maxDelayMs);
}

/**
 * Check if an error should trigger a retry
 * 
 * Examines error code to determine if the operation should be retried.
 * Permanent errors (permission denied, not found) fail fast.
 * Transient errors (network, timeout) are retried.
 * Unknown errors are not retried to be conservative.
 * 
 * @param {Error} error - The error that occurred
 * @param {Object} config - Retry configuration
 * @returns {boolean} True if should retry, false if should fail immediately
 * 
 * @example
 * const error = new Error('Service unavailable');
 * error.code = 'UNAVAILABLE';
 * shouldRetry(error) // true - will retry
 * 
 * const error2 = new Error('Permission denied');
 * error2.code = 'PERMISSION_DENIED';
 * shouldRetry(error2) // false - will fail fast
 */
export function shouldRetry(error, config = RETRY_CONFIG) {
    const errorCode = error.code || error.message || 'UNKNOWN';
    
    // Don't retry permanent errors
    if (config.nonRetryableErrorCodes.includes(errorCode)) {
        return false;
    }
    
    // Retry transient errors
    if (config.retryableErrorCodes.some(code => 
        errorCode.includes(code) || 
        (error.message && error.message.toLowerCase().includes(code.toLowerCase()))
    )) {
        return true;
    }
    
    // For unknown errors, be conservative and don't retry
    return false;
}
